"use client"

import { useMemo } from "react"
import { clsx } from "clsx"

import { useCart } from "../cartContext"

interface CartNavLabelProps {
    className?: string
}

function sumQuantities(items: { quantity: number }[]): number {
    return items.reduce((total, item) => total + (Number.isFinite(item.quantity) ? item.quantity : 0), 0)
}

export function CartNavLabel({ className }: CartNavLabelProps) {
    const { cart, loading } = useCart()

    const itemCount = useMemo(() => sumQuantities(cart.items), [cart.items])
    const showBadge = !loading && itemCount > 0
    const accessibleLabel = loading
        ? "Cart, updating"
        : itemCount > 0
        ? `Cart, ${itemCount} item${itemCount === 1 ? "" : "s"}`
        : "Cart, empty"

    return (
        <span
            className={clsx("flex flex-1 items-center justify-between", className)}
            aria-live="polite"
            role="status"
            aria-label={accessibleLabel}
        >
            <span>Cart</span>
            {loading ? (
                <span
                    aria-hidden="true"
                    className="h-5 w-5 animate-pulse rounded-full border border-white/60 dark:border-primary-200/70"
                />
            ) : showBadge ? (
                <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-amber-400 px-1.5 text-xs font-semibold text-primary-950">
                    {itemCount}
                </span>
            ) : null}
        </span>
    )
}
